import { create } from 'zustand';
import { Player, Mission, MissionInstance, MissionResult, Infection, getSoftwareResources } from '../types';
import { gameApi, softwareApi, fileSystemApi } from '../services/api';
import { syncScoreToArmorGames } from '../services/agi';
import { useTransferStore, justCompletedIds } from './transferStore';
import { useNotificationStore } from './notificationStore';

// Last score pushed to Armor Games, so we only call saveGame when it changes.
let lastSyncedScore = -1;

// IDs of pending downloads currently being confirmed via API.
// Prevents restoreDownloads from re-processing the same record while the
// completeDownload/confirmInstall request is still in-flight.
const confirmingIds = new Set<string>();

interface GameState {
  player: Player | null;
  // Infections running on the player's OWN node, from the dedicated container
  // (no longer part of the player document). Refreshed by loadPlayer().
  infections: Infection[];
  missions: Mission[];
  activeMissions: MissionInstance[];
  currentMission: Mission | null;
  isLoading: boolean;
  error: string | null;
}

interface GameStore extends GameState {
  loadPlayer: () => Promise<void>;
  loadMissions: () => Promise<void>;
  loadActiveMissions: () => Promise<void>;
  acceptMission: (missionId: string) => Promise<MissionInstance | null>;
  completeMission: (instanceId: string, viaAds?: boolean) => Promise<MissionResult | null>;
  cancelMission: (instanceId: string) => Promise<void>;
  setCurrentMission: (mission: Mission | null) => void;
  clearError: () => void;
}

export const useGameStore = create<GameStore>((set, get) => ({
  player: null,
  infections: [],
  missions: [],
  activeMissions: [],
  currentMission: null,
  isLoading: false,
  error: null,

  loadPlayer: async () => {
    set({ isLoading: true, error: null });
    try {
      // Player doc + own-node infections (dedicated container) in parallel; the
      // infection fetch is best-effort so it can never block the player load.
      const [response, infResp] = await Promise.all([
        gameApi.getPlayer(),
        gameApi.getInfections().catch(() => ({ data: [] as Infection[] })),
      ]);
      const player = response.data as Player;
      set({
        player,
        infections: (infResp.data ?? []) as Infection[],
        activeMissions: player.activeMissions ?? [],
        isLoading: false,
      });

      // Mirror the player's score to Armor Games (best-effort) so the AG-stored
      // value tracks our leaderboard's ranking metric. Only when it changed.
      if (player.reputation !== lastSyncedScore) {
        lastSyncedScore = player.reputation;
        void syncScoreToArmorGames({
          reputation: player.reputation,
          level: player.level,
          username: player.username,
        });
      }

      // Dispatch pending notifications as toasts.
      if (player.notifications?.length) {
        const { push } = useNotificationStore.getState();
        player.notifications.forEach(n => push(n));
      }

      // Restore any in-progress downloads that were running before this session.
      restoreDownloads(player, get);
    } catch {
      set({ error: 'Failed to load player data', isLoading: false });
    }
  },

  loadMissions: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await gameApi.getMissions();
      set({ missions: response.data, isLoading: false });
    } catch {
      set({ error: 'Failed to load missions', isLoading: false });
    }
  },

  loadActiveMissions: async () => {
    try {
      const response = await gameApi.getActiveMissions();
      set({ activeMissions: response.data ?? [] });
    } catch {
      // silent — active missions are also available via player doc
    }
  },

  acceptMission: async (missionId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await gameApi.acceptMission(missionId);
      const instance = response.data as MissionInstance;
      set(state => ({
        activeMissions: [...state.activeMissions, instance],
        isLoading: false,
      }));
      return instance;
    } catch (err: unknown) {
      const msg = extractError(err, 'Failed to accept mission');
      set({ error: msg, isLoading: false });
      return null;
    }
  },

  completeMission: async (instanceId: string, viaAds = false) => {
    set({ isLoading: true, error: null });
    try {
      const response = await gameApi.completeMission(instanceId, viaAds);
      const result = response.data as MissionResult;
      await get().loadPlayer();
      set({ isLoading: false });
      return result;
    } catch (err: unknown) {
      const msg = extractError(err, 'Failed to complete mission');
      set({ error: msg, isLoading: false });
      return null;
    }
  },

  cancelMission: async (instanceId: string) => {
    set({ isLoading: true, error: null });
    try {
      await gameApi.cancelMission(instanceId);
      // Optimistically drop it for a snappy UI, then reload the player so the
      // "mission cancelled" notification (added server-side, read+cleared by
      // GetPlayer) is fetched and toasted immediately instead of on the next refresh.
      set(state => ({
        activeMissions: state.activeMissions.filter(m => m.instanceId !== instanceId),
      }));
      await get().loadPlayer();
      set({ isLoading: false });
    } catch {
      set({ error: 'Failed to cancel mission', isLoading: false });
    }
  },

  setCurrentMission: (mission: Mission | null) => set({ currentMission: mission }),
  clearError: () => set({ error: null }),
}));

function extractError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } | string } };
  if (typeof e?.response?.data === 'string') return e.response.data;
  return e?.response?.data?.message ?? fallback;
}

// Restores download state after a page load / reconnect.
// Calculates how much time elapsed since each download started and either
// resumes it in the transfer store or immediately confirms completion.
function restoreDownloads(player: Player, get: () => GameStore) {
  const { addTransfer, transfers } = useTransferStore.getState();
  const now = Date.now();

  const completedSoftwareIds: string[] = [];
  const completedFiles: Array<{ id: string; fileName: string; content: string; sizeMb: number }> = [];

  // ── Software downloads ────────────────────────────────────────────────────
  for (const sw of player.software) {
    if (sw.isDownloaded || !sw.downloadStartedAt) continue;
    if (transfers.find(t => t.softwareId === sw.id)) continue;
    if (confirmingIds.has(sw.id) || justCompletedIds.has(`sw-${sw.id}`)) continue;

    const fileSizeMb   = getSoftwareResources(sw.type, sw.version).hddMb;
    const elapsedSec   = (now - new Date(sw.downloadStartedAt).getTime()) / 1000;
    const speedMbps    = (sw.downloadSpeedMbit ?? player.internetSpeedMBit) / 8;
    const downloadedMb = Math.min(elapsedSec * speedMbps, fileSizeMb);

    if (downloadedMb >= fileSizeMb) {
      completedSoftwareIds.push(sw.id);
    } else {
      addTransfer({
        id:                 `sw-${sw.id}`,
        kind:               'software',
        softwareId:         sw.id,
        softwareType:       sw.type,
        name:               sw.type,
        version:            sw.version,
        fileSizeMb,
        downloadedMb,
        effectiveSpeedMbit: sw.downloadSpeedMbit ?? undefined,
        isUpgrade:          sw.upgradeFromVersion != null,
      });
    }
  }

  // ── File downloads ────────────────────────────────────────────────────────
  for (const pd of player.pendingFileDownloads ?? []) {
    if (transfers.find(t => t.id === pd.id)) continue;
    if (confirmingIds.has(pd.id) || justCompletedIds.has(pd.id)) continue;

    const elapsedSec   = (now - new Date(pd.startedAt).getTime()) / 1000;
    const speedMbps    = pd.speedMbit / 8;
    const downloadedMb = Math.min(elapsedSec * speedMbps, pd.fileSizeMb);

    if (downloadedMb >= pd.fileSizeMb) {
      completedFiles.push({ id: pd.id, fileName: pd.fileName, content: pd.content, sizeMb: pd.fileSizeMb });
    } else {
      addTransfer({
        id:                 pd.id,
        kind:               'file',
        name:               pd.fileName,
        fileName:           pd.fileName,
        fileContent:        pd.content,
        fileSizeMb:         pd.fileSizeMb,
        downloadedMb,
        effectiveSpeedMbit: pd.speedMbit,
      });
    }
  }

  // Confirm any downloads that finished while the player was offline, then refresh.
  if (completedSoftwareIds.length > 0 || completedFiles.length > 0) {
    // Mark all as in-flight so concurrent loadPlayer calls skip them.
    completedSoftwareIds.forEach(id => confirmingIds.add(id));
    completedFiles.forEach(f => confirmingIds.add(f.id));

    const { push } = useNotificationStore.getState();
    const now = new Date().toISOString();

    completedSoftwareIds.forEach(sid => {
      const sw = player.software.find(s => s.id === sid);
      push({
        id: `sw-offline-${sid}`,
        type: 'download_complete',
        title: 'DOWNLOAD COMPLETE',
        message: sw
          ? `${sw.type} v${sw.version.toFixed(1)} (${getSoftwareResources(sw.type, sw.version).hddMb.toFixed(1)} MB) installed while you were offline.`
          : 'Software installed while offline.',
        createdAt: now,
      });
    });

    completedFiles.forEach(f => {
      push({
        id: `file-offline-${f.id}`,
        type: 'download_complete',
        title: 'FILE DOWNLOAD COMPLETE',
        message: `${f.fileName} (${f.sizeMb.toFixed(2)} MB) completed while offline, saved to local drive.`,
        createdAt: now,
      });
    });

    Promise.allSettled([
      ...completedSoftwareIds.map(sid => softwareApi.confirmInstall(sid)),
      ...completedFiles.map(f => fileSystemApi.completeDownload(f.fileName, f.content, f.sizeMb, f.id)),
    ]).then(() => {
      // Release guards before reloading so the fresh player is processed normally.
      completedSoftwareIds.forEach(id => confirmingIds.delete(id));
      completedFiles.forEach(f => confirmingIds.delete(f.id));
      get().loadPlayer();
    });
  }
}
